import { sourceQueryParamName } from 'RouteConstants';
import UAParser from 'ua-parser-js';

import { BaseTelemetryPlugin, IProcessTelemetryContext } from '@microsoft/applicationinsights-core-js';
import { Exception, ITelemetryItem, RemoteDependencyData } from '@microsoft/applicationinsights-web';

import { getCookie } from './Cookie';
import { pageDataService } from './PageDataService';
import { performanceService } from './PerformanceService';
import { ScubaTagValue, ServiceName, WebPlatform } from './TelemetryConstants';

/**
 * Parse the client user agent and return the parsed values
 *
 * @param clientUserAgent the client-user-agent passed by client
 */
export function parseClientUserAgent(clientUserAgent: string) {
  const [appVersionAndFlavor, os, OsVersion, DeviceId, Locale] = clientUserAgent.trim().split(/[ ();]+/);

  const [_, clientAppVersion, Flavor] = appVersionAndFlavor.match(/Defender\/([^-]*)-?(.*)/i) ?? [];

  return clientAppVersion ? { clientAppVersion, Flavor: Flavor || undefined, OsVersion, DeviceId, Locale } : {};
}

export class EnvironmentTelemetryPlugin extends BaseTelemetryPlugin {
  public priority = 1;

  // eslint-disable-next-line i18next/no-literal-string
  public identifier = 'EnvironmentTelemetryPlugin';

  public processTelemetry(event: ITelemetryItem, itemCtx?: IProcessTelemetryContext): void {
    const version: string = pageDataService.getApplicationVersion();
    const isWebView = pageDataService.isWebView();
    const userAgentWeb = 'Platform/Web';
    const clientUserAgentHeaderName = 'Client-User-Agent';
    const clientUserAgent = getCookie(clientUserAgentHeaderName);
    const uaParser = new UAParser();
    const uaParserResult = uaParser.getResult();
    const userAgent = uaParserResult?.ua;
    const queryString = pageDataService.getQueryStringForTelemetry();
    const queryParams = pageDataService.getQueryParametersForTelemetry();

    const data = {
      // Log `source` as NotificationSource if present
      ...(!!queryParams[sourceQueryParamName] && { NotificationSource: queryParams[sourceQueryParamName] }),

      // Allow event.data to override any of the above values
      ...(event.data ?? {}),

      ...(version && { ApplicationVersion: version }),
      ...(isWebView && clientUserAgent && { ClientUserAgent: clientUserAgent }),
      ...(!isWebView && { ClientUserAgent: userAgentWeb }),
      ...(userAgent && { userAgent }),
      ...parseClientUserAgent(clientUserAgent),
      ...(!!queryString && { QueryString: queryString }),
      ...queryParams,
      PageAge: performanceService.getElapsedTimeInMinutes().toFixed(2),
      Path: pageDataService.getPath(),
      AspNetCoreEnvironment: pageDataService.getEnvironment(),
      Locale: navigator.language,
      IsWebView: isWebView,
      HtmlVersion: pageDataService.getHtmlVersion(),
      Platform: isWebView ? window.nativeAppState.platform : WebPlatform,
      serviceName: ServiceName,
    };

    if (event.baseType === Exception.dataType) {
      const { message } = event.data ?? {};
      if (message === 'ErrorEvent: Script error.') {
        const scripts = document.getElementsByTagName('script');
        Object.assign(data, {
          Scripts: Array.from(scripts).map((script) => ({
            src: script.src,
            type: script.type,
            crossOrigin: script.crossOrigin,
            id: script.id,
          })),
        });
      }
    }

    if (event.baseType !== RemoteDependencyData.dataType) {
      Object.assign(data, { Tag: ScubaTagValue });
    }

    this.processNext(Object.assign(event, { data }), itemCtx as IProcessTelemetryContext);
  }
}
